import { Popover, PopoverProps, PopoverOrigin, PaperProps } from '@mui/material'
import { FC, ReactNode, useRef, useState, MouseEvent } from 'react'
import { DEFAULT_POPOVER_PAPER_PROPS, DEFAULT_POPOVER_PROPS } from './hoveredPopover.configs'

export interface HoveredPopoverProps {
  children: ReactNode | ((props: { onClose: () => void }) => ReactNode)
  trigger: (props: { onMouseEnter: (event: MouseEvent<HTMLElement>) => void; onMouseLeave: () => void }) => ReactNode
  closeDelay?: number
  anchorOrigin?: PopoverOrigin
  transformOrigin?: PopoverOrigin
  popoverProps?: Partial<PopoverProps>
  paperProps?: Partial<PaperProps>
  disabled?: boolean
  onMouseLeavePopover?: () => void
}

const HoveredPopover: FC<HoveredPopoverProps> = ({
  children,
  trigger,
  closeDelay = 200,
  anchorOrigin = {
    vertical: 'top',
    horizontal: 'right'
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'left'
  },
  popoverProps = DEFAULT_POPOVER_PROPS,
  paperProps = DEFAULT_POPOVER_PAPER_PROPS,
  disabled = false,
  onMouseLeavePopover
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const clearHoverTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) {
      return
    }
    clearHoverTimeout()
    setAnchorEl(event.currentTarget)
  }

  const handleMouseLeave = () => {
    if (disabled) {
      return
    }
    clearHoverTimeout()
    timeoutRef.current = setTimeout(() => {
      setAnchorEl(null)
    }, closeDelay)
  }

  // Keep popover open when hovering over it
  const handlePopoverMouseEnter = () => {
    if (disabled) {
      return
    }
    clearHoverTimeout()
  }

  // Close popover when leaving
  const handlePopoverMouseLeave = () => {
    if (disabled) {
      return
    }
    handleMouseLeave()
    if (onMouseLeavePopover) {
      onMouseLeavePopover()
    }
  }

  const open = Boolean(anchorEl) && !disabled

  return (
    <>
      {trigger({
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave
      })}
      <Popover
        disableRestoreFocus
        disableEnforceFocus
        open={open}
        anchorEl={anchorEl}
        onMouseEnter={handlePopoverMouseEnter}
        onMouseLeave={handlePopoverMouseLeave}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        PaperProps={paperProps}
        {...popoverProps}
      >
        {typeof children === 'function' ? children({ onClose: handleMouseLeave }) : children}
      </Popover>
    </>
  )
}

export default HoveredPopover
